import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { FilterMatchMode, FilterOperator } from 'primereact/api'
import {
  DataTable,
  DataTableBaseProps,
  DataTableFilterMeta,
  DataTableFilterMetaData,
  DataTableState,
  DataTableValueArray,
} from 'primereact/datatable'
import { useSessionstorageState } from 'rooks'
import { useImmer } from 'use-immer'

import {
  Roles,
  useAllRoles,
} from '../../../features/selfservice/roleAssignments/data'
import {
  SupplierList,
  useAllSuppliers,
} from '../../../features/selfservice/suppliers/data'
import { TableConfig } from '../../../features/ui-config'
import { Constants } from '../../constants'
import { getConfigTranslation } from '../../helper/getConfigTranslation'
import { EmptyMessage } from './EmptyMessage'
import { LoadingTable } from './LoadingTable'
import { createColumns, createHeader } from './create'
import { getFilterMatchMode } from './getter'

interface Props extends DataTableBaseProps<DataTableValueArray> {
  config: TableConfig
  data?: any[]
  dataKey?: string
  headerTitle?: string
  headerActions?: JSX.Element
  buttons?: JSX.Element
  checkboxen?: boolean
  link?: string
  selected?: any
  setSelected?: (params: any) => void
  globalFilterValue?: string
  isLoading?: boolean
  tableClassName?: string
  setGlobalFilterValue?:
    | React.Dispatch<React.SetStateAction<string>>
    | ((value: string) => void)
  stateKey: string
  children?: JSX.Element[]
  defaultFilters?: DataTableFilterMeta
  updateDefaultFilters?: (
    value: React.SetStateAction<DataTableFilterMeta>,
  ) => void
  clearDefaultFilter?: () => void
  navigationColumnHeader?: React.ReactNode
}

interface StateProps {
  stateKey?: string | undefined
  stateStorage?: 'session' | 'local' | 'custom' | undefined
  customRestoreState?(): object | undefined
  customSaveState?(state: object): void
}

export interface DropDownOptions {
  roles?: Roles
  suppliers?: SupplierList
}

export const ConfigurableTable = React.forwardRef<DataTable<any>, Props>(
  (
    {
      config,
      data,
      dataKey = 'Id',
      headerTitle,
      headerActions,
      buttons,
      checkboxen = false,
      link,
      selected,
      setSelected,
      globalFilterValue,
      isLoading,
      tableClassName,
      setGlobalFilterValue,
      stateKey,
      children,
      defaultFilters,
      updateDefaultFilters,
      clearDefaultFilter,
      navigationColumnHeader,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { data: roles } = useAllRoles()
    const { data: suppliers } = useAllSuppliers()
    const dropDownOptions: DropDownOptions = { roles, suppliers }

    const [tableState, setTableState] = useSessionstorageState<{
      [key: string]: DataTableState
    }>(Constants.SESSION_STORAGE_TABLE_KEY, {})

    const calculateTableStateMaxFirst = (
      dataLength: number,
      rows: number,
      first: number,
    ): number => {
      const maxFirst = Math.max(0, Math.floor((dataLength - 1) / rows) * rows)
      return Math.min(maxFirst, first)
    }

    const customRestoreState = () => {
      const currentState = tableState[stateKey] || null

      if (currentState && data) {
        currentState.first = calculateTableStateMaxFirst(
          data.length,
          currentState.rows,
          currentState.first,
        )
      }

      return currentState
    }

    const customSaveState = (state: DataTableState) => {
      if (JSON.stringify(state) !== JSON.stringify(tableState[stateKey])) {
        setTableState({ ...tableState, [stateKey]: state })
      }
    }

    const stateProps: StateProps = stateKey
      ? {
          stateStorage: 'custom',
          stateKey,
          customRestoreState,
          customSaveState,
        }
      : {}

    // filters
    const initFilters = () => {
      // create filter object for filtering otherwise if you click clear on the filter the app will crash
      const filterObject: DataTableFilterMeta = config.columns.reduce(
        (acc: any, column) => {
          const key = column.filterField || column.field!
          const operator = column.filter ? FilterOperator.AND : undefined
          const constraints: DataTableFilterMetaData[] = [
            {
              value: null,
              matchMode:
                column.filterMatchMode || getFilterMatchMode(column.dataType),
            },
          ]

          acc[key] = { operator, constraints }

          return acc
        },
        {},
      )

      // add global filter
      filterObject.global = {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      }

      return filterObject
    }

    const [filters, updateFilters] = useImmer<DataTableFilterMeta>(
      initFilters(),
    )

    const clearFilter = () => {
      if (clearDefaultFilter) {
        clearDefaultFilter()
        if (setGlobalFilterValue) {
          setGlobalFilterValue('')
        }
      } else {
        updateFilters(initFilters())
        if (setGlobalFilterValue) {
          setGlobalFilterValue('')
        }
      }
    }

    useEffect(() => {
      updateFilters(draft => {
        // @ts-ignore
        draft['global'].value = globalFilterValue
      })
    }, [isLoading, globalFilterValue, updateFilters])

    const emptyMessage = (
      <EmptyMessage
        title={getConfigTranslation(config?.emptyMessageTitle)}
        details={getConfigTranslation(config?.emptyMessageDetails)}
        value={data}
        clearFilter={clearFilter}
      />
    )

    function handleOnSelectionChange(e: any) {
      if (link) {
        // Verwende den dataKey-Wert anstatt hardcoded 'Id'
        navigate(`/${link}/${e.value[dataKey]}`)
      } else {
        if (setSelected) {
          setSelected(e.value)
        }
        return
      }
    }

    const header = createHeader(headerTitle, headerActions, config)

    // Loading
    if (isLoading) {
      return (
        <LoadingTable
          config={config}
          checkboxen={checkboxen}
          buttons={buttons}
          hasLink={!!link}
          header={header}
        />
      )
    }

    return (
      <>
        {config.enabled !== false && (
          <DataTable
            ref={ref}
            className={`mb-6 ${tableClassName}`}
            cellSelection={false}
            currentPageReportTemplate={t('common.table.numberOfEntries')}
            dataKey={dataKey}
            emptyMessage={emptyMessage}
            filterDisplay="menu"
            filters={defaultFilters || filters}
            onFilter={e => {
              if (updateDefaultFilters) {
                updateDefaultFilters(e.filters)
              } else {
                updateFilters(e.filters)
              }
            }}
            header={header}
            onSelectionChange={(e: any) => handleOnSelectionChange(e)}
            paginator={
              tableState[stateKey]?.rows === Constants.MAX_ROWS_FOR_PRINT
                ? false
                : true
            }
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            rows={tableState[stateKey]?.rows || config.rows}
            rowsPerPageOptions={config.rowsPerPageOptions}
            scrollable
            scrollHeight={
              tableState[stateKey]?.rows === Constants.MAX_ROWS_FOR_PRINT
                ? ''
                : config.scrollHeight || '50vh'
            }
            selection={selected}
            selectionMode={checkboxen ? 'checkbox' : 'single'}
            selectionPageOnly
            showGridlines
            sortField={config.sortField}
            sortOrder={config.sortOrder}
            value={data}
            sortMode={
              config?.multiSortMeta && config?.multiSortMeta?.length > 0
                ? 'multiple'
                : 'single'
            }
            multiSortMeta={config?.multiSortMeta}
            {...stateProps}
            {...props}
          >
            {/* columns */}
            {createColumns(
              config.columns,
              props,
              checkboxen,
              buttons,
              !!link,
              dropDownOptions,
              children,
              navigationColumnHeader,
            )}
          </DataTable>
        )}
      </>
    )
  },
)

ConfigurableTable.displayName = 'ConfigurableTable'
